// Generic retry helper for a call that can fail transiently — a momentary
// 5xx, a rate limit, a dropped connection — with no user-facing recourse
// otherwise. Lives here (not netlify/functions/_shared/) because it's
// genuinely used on both sides: originally added for the backend's own
// external API calls (chat/embeddings/Azure — see
// netlify/functions/_shared/chatModel.ts etc., which still import it via
// that directory's thin re-export), and now also by the frontend's own
// /ask stream reader (src/lib/askStream.ts) for the identical failure mode
// hitting the browser directly instead of a server-to-server call.

export class RetryableHttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "RetryableHttpError";
  }
}

const RETRYABLE_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

export function isRetryableStatus(status: number): boolean {
  return RETRYABLE_STATUSES.has(status);
}

interface RetryOptions {
  /** Total attempts including the first. Default 3 (1 try + 2 retries). */
  attempts?: number;
  /** Base delay before the first retry; doubles each subsequent attempt. Default 300ms. */
  baseDelayMs?: number;
  isRetryable?: (err: unknown) => boolean;
}

function defaultIsRetryable(err: unknown): boolean {
  if (err instanceof RetryableHttpError) return true;
  // fetch() throws a bare TypeError for network-level failures (DNS,
  // connection reset, timeout) — no HTTP status to check at all.
  if (err instanceof TypeError) return true;
  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function withRetry<T>(fn: () => Promise<T>, opts: RetryOptions = {}): Promise<T> {
  const attempts = opts.attempts ?? 3;
  const baseDelayMs = opts.baseDelayMs ?? 300;
  const isRetryable = opts.isRetryable ?? defaultIsRetryable;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt === attempts || !isRetryable(err)) throw err;
      const jitter = 0.75 + Math.random() * 0.5;
      const delay = baseDelayMs * 2 ** (attempt - 1) * jitter;
      console.warn(
        `Retrying transient failure (attempt ${attempt}/${attempts}, waiting ${Math.round(delay)}ms):`,
        err instanceof Error ? err.message : err,
      );
      await sleep(delay);
    }
  }
  // Unreachable: the loop above always either returns or throws.
  throw new Error("withRetry: exhausted attempts without a result");
}
